import React, { useState, useEffect, useCallback } from 'react';
import lpApi from '../../../helpers/lpApi';

// ============================================================================
// Big Projects · Disputes — admin list of open/under-review milestone disputes
// for Large Projects, with one-click resolution.
//
// Consumes besthand-api `listOpenDisputes` through the `/lp-graphql` proxy via
// `lpApi`. Resolution runs `resolveMilestoneDispute` (admin-gated server-side
// via requireAdmin + gateLargeProjects); the proxy just relays auth.
// ============================================================================

const Q_DISPUTES = `
  query {
    listOpenDisputes {
      status errorMessage
      disputes {
        id projectContractId projectMilestoneId openedByUserId
        reason description status resolution refundAmount createdAt
      }
    }
  }
`;

// resolveMilestoneDispute(disputeId!, decision!, refundAmount, resolution)
// decision ∈ RESOLVED_CLIENT | RESOLVED_COMPANY | RESOLVED_PARTIAL
const M_RESOLVE = `
  mutation ($disputeId: String!, $decision: String!, $refundAmount: Float, $resolution: String) {
    resolveMilestoneDispute(disputeId: $disputeId, decision: $decision, refundAmount: $refundAmount, resolution: $resolution) {
      status errorMessage
      dispute { id status resolution refundAmount }
    }
  }
`;

const S = {
  page: { maxWidth: 1080, margin: '0 auto', padding: 24, fontFamily: "'Inter', -apple-system, sans-serif", color: '#0f1214' },
  h2: { margin: '0 0 4px', fontSize: 24, fontWeight: 800 },
  hint: { color: '#6b7280', fontSize: 14, marginBottom: 20 },
  toolbar: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 18, flexWrap: 'wrap' },
  btnGhost: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  card: { border: '1px solid #e7e9ec', borderRadius: 14, padding: '18px 20px', marginBottom: 14, boxShadow: '0 6px 18px rgba(15,18,20,0.03)' },
  rowTop: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 },
  name: { fontSize: 17, fontWeight: 800, margin: 0 },
  meta: { color: '#6b7280', fontSize: 13, marginTop: 4, lineHeight: 1.5 },
  desc: { fontSize: 14, color: '#0f1214', marginTop: 10, lineHeight: 1.5 },
  badge: (kind) => {
    const map = {
      OPEN: { bg: '#fff3d6', fg: '#946200' },
      UNDER_REVIEW: { bg: '#eef2ff', fg: '#3730a3' },
      RESOLVED: { bg: '#e7f6ec', fg: '#137a37' },
      REJECTED: { bg: '#fde7e8', fg: '#c2151c' },
    };
    const c = map[kind] || { bg: '#eef2f4', fg: '#5a6168' };
    return { fontSize: 11, fontWeight: 800, letterSpacing: '0.05em', padding: '4px 10px', borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: 'nowrap' };
  },
  empty: { color: '#6b7280', fontSize: 14, padding: '40px 0', textAlign: 'center' },
  alert: (ok) => ({ padding: '10px 14px', borderRadius: 8, fontSize: 14, marginBottom: 16, background: ok ? '#ecfdf5' : '#fef2f2', color: ok ? '#065f46' : '#991b1b' }),
  actions: { display: 'flex', gap: 8, marginTop: 14, flexWrap: 'wrap' },
  btn: { background: '#16a34a', color: '#fff', border: 'none', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnRed: { background: '#fff', color: '#c2151c', border: '1px solid #f3c9cb', borderRadius: 9, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  btnGhostSm: { background: '#fff', color: '#0f1214', border: '1px solid #e7e9ec', borderRadius: 9, padding: '8px 14px', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
};

const fmtMoney = (v) => (v == null ? null : `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const fmtDate = (s) => { if (!s) return ''; try { return new Date(s).toLocaleString(); } catch (e) { return s; } };

function Disputes() {
  const [disputes, setDisputes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [msg, setMsg] = useState(null);

  const firstError = (res) => (res && res.errors && res.errors.length ? res.errors[0].message : 'Something went wrong.');

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const res = await lpApi(Q_DISPUTES);
      if (res.errors) {
        setMsg({ error: firstError(res) });
      } else {
        const env = res.data && res.data.listOpenDisputes;
        if (env && env.status === 200) setDisputes(env.disputes || []);
        else if (env) setMsg({ error: env.errorMessage || `Error ${env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Could not load disputes: ${e.message}` });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (key, query, variables, opName, okText) => {
    setBusyId(key);
    setMsg(null);
    try {
      const res = await lpApi(query, variables);
      if (res.errors) { setMsg({ error: firstError(res) }); setBusyId(''); return; }
      const env = res.data && res.data[opName];
      if (env && env.status >= 200 && env.status < 300) {
        setMsg({ ok: okText });
        await load();
      } else {
        setMsg({ error: (env && env.errorMessage) || `Error ${env && env.status}` });
      }
    } catch (e) {
      setMsg({ error: `Action failed: ${e.message}` });
    }
    setBusyId('');
  };

  const isOpen = (d) => d.status === 'OPEN' || d.status === 'UNDER_REVIEW';

  const resolve = (d, decision) => {
    const labels = { RESOLVED_CLIENT: 'in the client’s favor (full refund)', RESOLVED_COMPANY: 'in the company’s favor (release funds)', RESOLVED_PARTIAL: 'with a partial refund' };
    let refundAmount = null;
    if (decision === 'RESOLVED_PARTIAL') {
      const raw = typeof window !== 'undefined' ? window.prompt(`Partial resolution for dispute #${d.id}. Refund amount to the client (USD):`) : '';
      if (raw === null) return;
      refundAmount = Number(raw);
      if (!Number.isFinite(refundAmount) || refundAmount <= 0) { setMsg({ error: 'Refund amount must be a number greater than 0.' }); return; }
    }
    const resolution = typeof window !== 'undefined' ? window.prompt(`Resolving dispute #${d.id} ${labels[decision]}.\n\nOptional resolution note (leave blank to skip):`) : '';
    if (resolution === null) return; // admin cancelled
    run(
      `d${d.id}`,
      M_RESOLVE,
      { disputeId: String(d.id), decision, refundAmount, resolution: resolution.trim() ? resolution.trim() : null },
      'resolveMilestoneDispute',
      `Dispute #${d.id} resolved (${decision.replace('RESOLVED_', '').toLowerCase()}).`,
    );
  };

  return (
    <div style={S.page}>
      <h2 style={S.h2}>Disputes</h2>
      <div style={S.hint}>
        Large Projects — open and under-review milestone disputes, oldest first. Resolve in the client’s favor
        (full refund), the company’s favor (release funds), or with a partial refund.
      </div>

      <div style={S.toolbar}>
        <button style={S.btnGhost} onClick={() => load()} disabled={loading}>
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
        <span style={{ color: '#6b7280', fontSize: 13 }}>{disputes.length} open</span>
      </div>

      {msg && <div style={S.alert(!!msg.ok)}>{msg.ok || msg.error}</div>}
      {loading && <div style={S.empty}>Loading…</div>}

      {!loading && (
        disputes.length === 0 ? (
          <div style={S.empty}>No open disputes. 🎉</div>
        ) : disputes.map((d) => (
          <div style={S.card} key={d.id}>
            <div style={S.rowTop}>
              <div>
                <p style={S.name}>Dispute #{d.id} · {d.reason || 'No reason'}</p>
                <div style={S.meta}>
                  Contract #{d.projectContractId}
                  {d.projectMilestoneId ? ` · Milestone #${d.projectMilestoneId}` : ''}
                  {d.openedByUserId ? ` · Opened by ${d.openedByUserId}` : ''}
                  {d.createdAt ? ` · ${fmtDate(d.createdAt)}` : ''}
                  {d.refundAmount != null ? ` · Refund requested ${fmtMoney(d.refundAmount)}` : ''}
                </div>
                {d.description ? <div style={S.desc}>{d.description}</div> : null}
                {d.resolution ? <div style={S.meta}>Resolution: {d.resolution}</div> : null}
              </div>
              <span style={S.badge(d.status)}>{d.status || 'OPEN'}</span>
            </div>
            {isOpen(d) && (
              <div style={S.actions}>
                <button style={S.btn} disabled={busyId === `d${d.id}`} onClick={() => resolve(d, 'RESOLVED_CLIENT')}>
                  {busyId === `d${d.id}` ? '…' : 'Resolve · Client'}
                </button>
                <button style={S.btnGhostSm} disabled={busyId === `d${d.id}`} onClick={() => resolve(d, 'RESOLVED_COMPANY')}>
                  Resolve · Company
                </button>
                <button style={S.btnRed} disabled={busyId === `d${d.id}`} onClick={() => resolve(d, 'RESOLVED_PARTIAL')}>
                  Resolve · Partial
                </button>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}

export default Disputes;
